<template>
  <section class="w-full">
    <!-- =========================
         BLOQUE 1: Imagen circular + título + bajada
         (Desktop 2 columnas / Mobile apilado)
         ========================= -->
    <div
      class="
        w-full
        px-[20px] py-[30px]
        md:py-[70px] md:px-[60px]
        xl:px-[130px]
        2xl:px-[190px]
        max-w-[1400px] mx-auto
      "
    >
      <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-[60px] gap-y-[18px] md:gap-y-0 max-w-[1400px] mx-auto">
        <!-- Imagen circular -->
        <div class="flex items-center justify-center md:justify-center">
          <img
            :src="assets.imagen_circular"
            alt="Amortiguador (imagen referencial)"
            class="
              rounded-full object-cover
              w-[260px] h-[260px]
              md:w-[320px] md:h-[320px]
            "
            loading="lazy"
            decoding="async"
          />
        </div>

        <!-- Texto -->
        <div class="flex flex-col justify-center">
          <div class="flex items-start gap-2">
            <!-- Flecha (negra) -->
            <span class="-mt-[6px] inline-flex text-black" aria-hidden="true">
              <svg width="44" height="50" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>

            <h2
              class="
                font-bold text-black
                text-[26px] leading-[1.15]
                md:text-[32px]
              "
            >
              ¿Cuáles son sus averías<br class="hidden md:block" />
              más comunes y sus causas?
            </h2>
          </div>

          <p class="mt-[14px] text-[16px] leading-[1.65] text-[#3a3a3a] max-w-[520px]">
            Para mantener los amortiguadores en buen estado, resulta importante realizar una revisión cada
            10.000 kilómetros aproximadamente, pues es habitual que éstos vayan perdiendo su eficacia con el
            paso del tiempo.
          </p>
        </div>
      </div>
    </div>

    <!-- =========================
         BLOQUE 2: Fondo gris + grilla de indicios (5)
         Desktop 3 cols (3 + 2)
         Mobile 2 cols y último a ancho completo
         ========================= -->
    <div
      class="
        w-full bg-[#F1F2F2]
        px-[20px] py-[30px]
        md:py-[70px] md:px-[60px]
        xl:px-[130px]
        2xl:px-[190px]
      "
    >
      <h2
        class="
          text-center text-black
          text-[32px] leading-[1.25]
          md:text-[28px]
          font-bold
          mb-[40px]
          max-w-[1400px] mx-auto
        "
      >
        Algunos de los indicios que nos pueden hacer sospechar<br class="hidden md:block" />
        de un mal funcionamiento son los siguientes:
      </h2>

      <div
        class="
          grid
          grid-cols-2
          md:grid-cols-3
          gap-x-[26px] gap-y-[36px]
          md:gap-x-[60px] md:gap-y-[54px]
           max-w-[1400px] mx-auto
        "
      >
        <article
          v-for="(item, idx) in indicios"
          :key="item.titulo"
          :class="[
            'min-w-0',
            idx === indicios.length - 1 ? 'col-span-2 md:col-span-1' : ''
          ]"
        >
          <img
            :src="item.icono"
            alt=""
            class="w-[74px] h-[74px] object-contain"
            loading="lazy"
            decoding="async"
          />

          <h3
            class="
              mt-[10px]
              font-bold text-black
              text-[18px] leading-[1.15]
              md:text-[28px]
            "
          >
            {{ item.titulo }}
          </h3>

          <p class="mt-[10px] text-[13px] md:text-[16px] leading-[1.65] text-[#3a3a3a]">
            {{ item.descripcion }}
          </p>
        </article>
      </div>
    </div>

    <!-- =========================
         BLOQUE 3: Imagen fondo izquierda + caja gris oscura derecha
         Desktop 2 columnas / Mobile apilado
         ========================= -->
    <div class="w-full mt-6">
      <div class="grid grid-cols-1 md:grid-cols-2 max-w-[1400px] mx-auto rounded-xl overflow-hidden border border-black/20">

        <!-- Imagen (background) -->
        <div
          class="
            w-full
            min-h-[450px]
            md:min-h-[520px]
            bg-cover bg-center
          "
          :style="{
            backgroundImage: `url(${assets.fondo_amortiguador})`
          }"
          role="img"
          aria-label="Técnico trabajando con amortiguador (imagen referencial)"
        />

        <!-- Caja texto -->
        <div
          class="
            w-full bg-[#363636] text-white
            px-[25px] py-[22px]
            md:px-[60px] md:py-[60px]
            2xl:px-[150px] 2xl:py-[100px]
            flex flex-col justify-center
          "
        >
          <div class="flex items-start gap-2">
            <!-- Flecha (blanca) -->
            <span class="-mt-[6px] inline-flex text-white" aria-hidden="true">
              <svg width="44" height="50" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>

            <h2
              class="
                font-bold
                text-[32px] leading-[1.2]
                md:text-[26px]
              "
            >
              ¿Cuándo realizar un cambio de amortiguadores?
            </h2>
          </div>

          <p class="mt-[14px] text-[13px] md:text-[16px] leading-[1.75] text-white/95">
            La duración de los amortiguadores es muy variable, pues depende del tipo de vehículo y la
            superficie habitual en que es utilizado (carretera, caminos de tierra, campo, ciudad, etc.) y
            también según la función que se le dé (pasajero, carga, etc.). Siempre es importante fijarse en lo
            que estipula el manual del fabricante.
            <br /><br />
            En caso de no reemplazarlos cuando es necesario, el vehículo comenzará a empeorar su comportamiento,
            haciéndose más incómodo, alargando las distancias de frenado y el agarre en curvas o la hora de
            esquivar obstáculos. Además, los neumáticos perderán vida útil.
            <br /><br />
            Finalmente, resulta muy importante cambiar los amortiguadores de un mismo eje a la vez, garantizando
            así la estabilidad de dicho eje al presentar ambos lados el mismo comportamiento.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
// =====================================================
// Sección informativa Amortiguadores (Nuxt3/Vue3 + Tailwind)
// - 1 archivo, sin dependencias externas nuevas.
// - Clases con medidas "pixel-oriented" para acercarse al legacy.
// =====================================================

const assets = {
  imagen_circular: '/img/amortiguadores/amortiguador_circulo.png',
  fondo_amortiguador: '/img/amortiguadores/fondo_amortiguador.jpg'
}

const indicios = [
  {
    icono: '/img/amortiguadores/ico1.png',
    titulo: 'Desgaste de los neumáticos',
    descripcion:
      'Si existe un desgaste irregular, a pesar de mantenerse unos niveles de presión constantes, lo más probable es que haya un desajuste en la suspensión.'
  },
  {
    icono: '/img/amortiguadores/ico2.png',
    titulo: 'Vibraciones en el volante',
    descripcion:
      'Fundamentalmente, si se produce una vibración al frenar en curvas, el amortiguador delantero del lado exterior o su correspondiente ballesta estarán en mal estado.'
  },
  {
    icono: '/img/amortiguadores/ico3.png',
    titulo: 'Inclinación excesiva',
    descripcion:
      'Cuando al girar notamos que la carrocería se inclina demasiado, es necesario revisar el sistema hidráulico, la barra estabilizadora y los muelles.'
  },
  {
    icono: '/img/amortiguadores/ico4.png',
    titulo: 'Cabeceo',
    descripcion:
      'Si la parte delantera del vehículo baja en exceso cuando frenamos enérgicamente y vuelve a su posición posteriormente, puede deberse a un desgaste excesivo de los amortiguadores.'
  },
  {
    icono: '/img/amortiguadores/ico5.png',
    titulo: 'Rebote',
    descripcion:
      'Si durante la conducción la rueda rebota sobre la superficie de contacto, es necesario cambiar los amortiguadores. También se puede comprobar presionando sobre la carrocería. Si rebota más de una vez, es conveniente hacer una revisión.'
  }
]
</script>
